import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { StatusCode } from 'status-code-enum';
import { ApiError } from '#~/api/types.js';
import {
  createRoom,
  deleteRoom,
  ensureInitialRoom,
  fetchRoomById,
  fetchRememberedStudioForAgent,
  fetchRoomIdForAgentId,
  setRememberedStudioForAgent
} from '#~/api/agent/registry.js';
import type {
  EnsureInitialRoomInventoryOrganization,
  EnsureInitialRoomInventoryRoom
} from '#~/api/agent/registry.js';
import { toIdFromHex } from '#~/api/utils.js';
import { config } from '#~/config/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import * as persistenceLayer from '#~/persistence/index.js';
import { shouldAutoLink } from './auto-link-vendor.js';

export interface RoomCheckResult {
  readonly alreadyLinked: boolean;
  readonly roomId: Room['id'] | undefined;
  /**
   * ENG-4154 warm account-switch discriminator. The registry's agents/get
   * Lambda runs its policy check BEFORE the existence lookup
   * (api/common.api.agent-registry/registry/agents/get/main.go), so an
   * in-band 403 means "this agent exists under an org the current account
   * cannot access" — definitive cross-account evidence — while 404 means
   * "agent not found". Both collapse to `alreadyLinked: false` for the link
   * decision, but the 403 must survive for the stale cross-account link
   * detection in the auto-link controller.
   */
  readonly forbidden?: boolean;
}

export type RoomCreationResult =
  | {
      readonly status: 'room';
      readonly roomId: Room['id'];
      readonly created: boolean;
      readonly authoritativeRoom?: EnsureInitialRoomInventoryRoom;
      readonly authoritativeOwnedOrganizations?: readonly EnsureInitialRoomInventoryOrganization[];
    }
  | {
      readonly status: 'inventory_changed';
      readonly roomCount: number;
      readonly rooms: readonly EnsureInitialRoomInventoryRoom[];
      readonly organizationCount: number;
      readonly ownedOrganizations: readonly EnsureInitialRoomInventoryOrganization[];
    };

export type CreateAutoLinkRoomOptions = {
  readonly idempotencyKey?: string;
};

/**
 * Check if a bridge is already linked to a room using direct server lookup.
 * Uses O(1) API call via AgentRegistryGetAgent instead of listing all rooms.
 */
export const checkBridgeLinkedRoom = async (
  bridgeId: Agent['id'],
  token: string
): Promise<RoomCheckResult> => {
  try {
    const roomId = await fetchRoomIdForAgentId(bridgeId, token);

    if (roomId) {
      return { alreadyLinked: true, roomId };
    }

    return { alreadyLinked: false, roomId: undefined };
  } catch (error) {
    if (
      error instanceof ApiError &&
      (error.code === StatusCode.ClientErrorNotFound ||
        error.code === StatusCode.ClientErrorForbidden)
    ) {
      return {
        alreadyLinked: false,
        roomId: undefined,
        forbidden: error.code === StatusCode.ClientErrorForbidden
      };
    }

    throw error;
  }
};

/**
 * ENG-4166 D2 room-access verdict. `checkBridgeLinkedRoom` proves the AGENT
 * link exists but NOT that the linked ROOM is reachable under the current
 * token — a cross-account link reads the agent yet 403s the room (retry-2
 * signature). `accessible` = the current token can open the room; `forbidden`
 * = a DEFINITIVE 403 (cross-account policy fact); `unknown` = a 404 / transport
 * hiccup that must NOT be treated as a hard cross-account fact (never destroy a
 * healthy link on registry weather).
 */
export type RoomAccess = 'accessible' | 'forbidden' | 'unknown';

/**
 * Verify the CURRENT token can actually open `roomId`. Auto-link `success` must
 * imply room access (ENG-4166 D2), so the controller gates every success on
 * this. `fetchRoomById` returns the room on 200, `undefined` on 404, and throws
 * on 403 — so a 403 maps to `forbidden` (cross-account) while everything else
 * that is not a clean 200 maps to `unknown` (ambiguous / weather).
 * Per rule: reference-rules-in-comments (ai.knowledge/global) — D2 access gate.
 */
export const checkRoomAccessible = async (
  roomId: Room['id'],
  token: string
): Promise<RoomAccess> => {
  try {
    const room = await fetchRoomById(roomId, token);
    return room ? 'accessible' : 'unknown';
  } catch (error) {
    if (error instanceof ApiError && error.code === StatusCode.ClientErrorForbidden) {
      return 'forbidden';
    }
    // 404 is swallowed by fetchRoomById (returns undefined); any other throw is
    // transport weather — ambiguous, never a hard cross-account verdict.
    return 'unknown';
  }
};

/**
 * Create a new room for auto-linking.
 * Room name should already be resolved by the controller, preferring the current bridge name
 * with hostname and generic fallbacks applied there.
 */
export const createAutoLinkRoom = async (
  token: string,
  studioName: string,
  optionsOrIdempotencyKey: CreateAutoLinkRoomOptions | string = {}
): Promise<RoomCreationResult> => {
  // Accept the string form used by the retrying controller while retaining the
  // options form used by the authoritative account-ensure path.
  const options: CreateAutoLinkRoomOptions =
    typeof optionsOrIdempotencyKey === 'string'
      ? { idempotencyKey: optionsOrIdempotencyKey }
      : optionsOrIdempotencyKey;
  if (!options.idempotencyKey) {
    throw new Error('Initial Studio requires an idempotency key');
  }

  // E2E STEER (ENG cross-account-collision fix): when the e2e harness has
  // pinned a per-run org via the @@ls.e2eAutoLinkOrgId persistence key, use
  // THAT org directly and skip the account-level ensureInitialRoom boundary
  // below. The shared e2e Auth0 account accumulates many orgs across
  // concurrent CI runs; without pinning, two runs converge on the SAME
  // account inventory and their agents collide (CloudWatch: 3,494 linkAgentV2
  // across shared accounts → 403 → "Cross-account bridge link detected" → B2
  // "Unable to connect" → timeout). global-setup creates a fresh org per run
  // and seeds its hex id here. Gated strictly to the auto-link eligibility
  // envelope (Electron + iris vendor) so production is untouched — the key
  // simply does not exist in production stores. See
  // studio/electron/e2e/global-setup.ts (provisionPerRunOrg) +
  // fixtures/org/run-scope.ts.
  // Per rule: reference-rules-in-comments + code-comments-required
  //   (ai.knowledge/global); stay-in-scope (single guarded read, prod path
  //   unchanged). The eligibility envelope routes through the single fail-closed
  //   vendor decision (shouldAutoLink) so an unknown / missing / garbage vendor
  //   can never trip the e2e org-pin steer — it is gated to genuine iris only.
  if (shouldAutoLink({ isElectron: isElectronApp(), vendor: config.vendor })) {
    const e2eOrgHex = await persistenceLayer.get('e2eAutoLinkOrgId');
    if (e2eOrgHex) {
      const pinnedOrgId = toIdFromHex(e2eOrgHex);
      if (pinnedOrgId) {
        const { id: roomId, alreadyExisted } = await createRoom(
          studioName,
          token,
          pinnedOrgId,
          options.idempotencyKey
        );
        // created=false when the server deduped an idempotent retry (ENG-4209).
        return { status: 'room', roomId, created: !alreadyExisted };
      }
    }
  }

  // This is the authoritative 0 -> 1 boundary. The endpoint owns organization
  // selection/creation and the primary-database account lock; never fall back
  // to the legacy client-side create sequence when the endpoint is unavailable.
  const result = await ensureInitialRoom(studioName, token, options.idempotencyKey);
  if (result.status === 'inventory_changed') {
    return result;
  }

  return {
    status: 'room',
    roomId: result.roomId,
    created: result.status === 'created',
    authoritativeRoom: result.room,
    authoritativeOwnedOrganizations: result.ownedOrganizations
  };
};

/**
 * Get or create a room for auto-linking.
 * First checks if bridge is already linked, then creates new room if needed.
 *
 * Currently not being used, however, leaving around just in case we need this functionality
 */
export const getOrCreateAutoLinkRoom = async (
  bridgeId: Agent['id'],
  token: string,
  studioName: string
): Promise<RoomCreationResult & { alreadyLinked: boolean }> => {
  const checkResult = await checkBridgeLinkedRoom(bridgeId, token);

  if (checkResult.alreadyLinked && checkResult.roomId) {
    return {
      status: 'room',
      roomId: checkResult.roomId,
      created: false,
      alreadyLinked: true
    };
  }

  // The bridge ID is the only stable logical identity available to this legacy
  // helper. Production uses the link-request-scoped key from the controller.
  const createResult = await createAutoLinkRoom(token, studioName, {
    idempotencyKey: `auto-link:legacy:${bridgeId}`
  });

  return {
    ...createResult,
    alreadyLinked: false
  };
};

/**
 * Read the ENG-4037 remembered-Studio preference for a bridge. ADVISORY —
 * any failure (404 agent-not-yet-created, 403 cross-account, registry
 * weather) degrades to "no preference"; the caller falls back to the picker.
 * Never throws: a broken preference read must not break linking.
 */
export const fetchRememberedStudio = async (
  bridgeId: Agent['id'],
  requestId: Parameters<typeof fetchRememberedStudioForAgent>[1],
  token: string
): Promise<Room['id'] | undefined> => {
  try {
    return (await fetchRememberedStudioForAgent(bridgeId, requestId, token)) ?? undefined;
  } catch (error) {
    console.warn('[auto-link-room] remembered-studio read failed (non-fatal):', error);
    return undefined;
  }
};

/**
 * Persist the ENG-4037 remember-my-choice preference after a successful link.
 * BEST-EFFORT — a failed write only costs the user one extra picker next
 * boot; it must never fail the link that just succeeded.
 */
export const persistRememberedStudio = async (
  bridgeId: Agent['id'],
  roomId: Room['id'] | undefined,
  token: string
): Promise<void> => {
  try {
    await setRememberedStudioForAgent(bridgeId, roomId, token);
    console.log('[auto-link-room] Remembered studio preference updated for bridge:', bridgeId);
  } catch (error) {
    console.warn('[auto-link-room] Failed to persist remembered studio (non-fatal):', error);
  }
};

/**
 * Delete a room created during auto-link if linking fails.
 * Used for cleanup to prevent orphaned rooms.
 */
export const deleteAutoLinkRoom = async (roomId: Room['id'], token: string): Promise<void> => {
  try {
    await deleteRoom(roomId, token);
  } catch (error) {
    console.error('[auto-link-room] Failed to cleanup room:', roomId, error);
  }
};
